import DeselectIcon from "@mui/icons-material/Deselect";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import * as React from "react";

import { Button } from "@/shared/foundation/primitives/Button";
import { usePopupState } from "@/shared/hooks/usePopupState";

import styles from "./DataTableSelection.module.css";

export interface SelectionOption<T> {
  /**
   * Label shown for the menu item. Should be unique per option.
   */
  name: string;

  /**
   * Action fired when the user picks this option. Receives the full
   * accumulated cross-page selection.
   *
   * Consumers own error handling. The primitive does NOT catch
   * rejections from `action`.
   */
  action: (args: { selected: T[] }) => void | Promise<void>;

  /** Optional leading icon. */
  icon?: React.ReactNode;
}

export interface DataTableSelectionProps<T> {
  /**
   * Current cross-page selection. Sourced from
   * `useDataTableState().selectionSlice.selection`.
   */
  selection: T[];

  /**
   * Clears the entire selection.
   */
  clearSelection: () => void;

  /**
   * Bulk actions the consumer offers.
   */
  options: SelectionOption<T>[];
}

/**
 * "Selection" trigger + popover menu for the `DataTable.Actions` row.
 * Renders nothing when `options` is empty (consumer hasn't wired any
 * bulk actions) or when `selection` is empty — the button only appears
 * once the user has selected at least one row, so an empty selection
 * never produces a dead affordance in the toolbar.
 */
export function DataTableSelection<T>({
  selection,
  clearSelection,
  options,
}: DataTableSelectionProps<T>) {
  const popup = usePopupState();

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (popup.isOpen) {
      popup.close();
    } else {
      popup.open({ anchorEl: event.currentTarget });
    }
  };

  const handleDeselectAll = () => {
    popup.close();
    clearSelection();
  };

  const handleOptionClick = async (option: SelectionOption<T>) => {
    popup.close();
    await option.action({ selected: selection });
    // Clear selection on success
    clearSelection();
  };

  if (options.length === 0 || selection.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleTriggerClick}
        className={styles.trigger}
        endIcon={<KeyboardArrowDownIcon />}
        aria-haspopup="menu"
        aria-expanded={popup.isOpen}
      >
        <span className={styles.triggerLabel}>Selected</span>
        <span className={styles.triggerCount} aria-hidden="true">
          {selection.length}
        </span>
      </Button>
      <Menu
        className={styles.menu}
        anchorEl={popup.anchor}
        open={popup.isOpen}
        onClose={popup.close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={handleDeselectAll}>
          <DeselectIcon fontSize="small" />
          Deselect All
        </MenuItem>
        {options.map((option) => (
          <MenuItem
            key={option.name}
            onClick={() => {
              void handleOptionClick(option);
            }}
          >
            {option.icon}
            {option.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
